import { Routes } from '@angular/router';
import { MainComponent } from 'app/main-container/main-container.component';
import { authGuard } from 'app/shared/guards/auth.guard';
import { dashboardAccessGuard } from 'app/shared/guards/dashboard-access.guard';
import { featureFlagGuard } from 'app/shared/guards/feature-flag.guard';
import { isIndagoAiTenantGuard } from 'app/shared/guards/indago-ai-tenant.guard';
import { permissionGuard } from 'app/shared/guards/permission.guard';
import { NotFoundComponent } from './shared/components/not-found/not-found.component';
import { FeatureFlagType } from './shared/models/config/feature-flag.type';
import { environment } from '@envs/environment';

export const routes: Routes = [
  {
    path: '',
    component: MainComponent,
    canActivate: [dashboardAccessGuard],
    children: [
      { path: '', redirectTo: environment.defaultRoute, pathMatch: 'full' },
      {
        path: 'dashboard',
        canActivate: [featureFlagGuard],
        data: {
          featureFlag: FeatureFlagType.DASHBOARD_ENABLED,
        },
        loadComponent: () => import('./dashboard/dashboard.component').then((it) => it.DashboardComponent),
      },
      {
        path: 'sales',
        canActivate: [featureFlagGuard],
        data: {
          featureFlag: FeatureFlagType.SALE_ENABLED,
        },
        loadComponent: () => import('./sales/sales.component').then((it) => it.SalesComponent),
        children: [
          {
            path: '',
            redirectTo: 'widgets',
            pathMatch: 'full',
          },
          {
            path: 'widgets',
            loadComponent: () =>
              import('./sales/components/widgets/widgets-preview/widgets-preview.component').then((it) => it.WidgetsPreviewComponent),
          },
          {
            path: 'details/:id',
            loadComponent: () => import('./sales/components/widgets/widget-details/widget-details.component').then((it) => it.WidgetDetailsComponent),
          },
        ],
      },
      {
        path: 'projects',
        canActivate: [permissionGuard],
        data: {
          permissionKey: 'research_assistant',
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./projects/projects.component').then((it) => it.ProjectsComponent),
          },
          {
            path: 'details/:projectId',
            loadComponent: () => import('./projects/components/project-details/project-details.component').then((it) => it.ProjectDetailsComponent),
            children: [
              { path: '', redirectTo: 'assistant', pathMatch: 'full' },
              {
                path: 'assistant',
                loadChildren: () => import('./projects/routes/assistant.routes').then((it) => it.assistantRoutes),
              },
              {
                path: 'settings',
                loadComponent: () => import('./projects/components/project-details/settings/settings.component').then((it) => it.SettingsComponent),
              },
            ],
          },
        ],
      },
      {
        path: 'team',
        canActivate: [featureFlagGuard],
        data: {
          featureFlag: FeatureFlagType.TEAM_PAGE_ENABLED,
        },
        loadComponent: () => import('./team/team.component').then((it) => it.TeamComponent),
      },
      {
        path: 'settings',
        canActivate: [featureFlagGuard],
        data: {
          featureFlag: FeatureFlagType.PROFILE_SETTINGS_ENABLED,
        },
        loadComponent: () => import('./user-settings/user-settings.component').then((it) => it.UserSettingsComponent),
      },
      {
        path: 'billing',
        canActivate: [featureFlagGuard],
        data: {
          featureFlag: FeatureFlagType.BILLINGS_PAGE_ENABLED,
        },
        loadComponent: () => import('./billing/billing.component').then((it) => it.BillingComponent),
      },
      {
        path: 'scholar',
        canActivate: [featureFlagGuard],
        data: {
          featureFlag: FeatureFlagType.SCHOLAR_ENABLED,
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./scholar/scholar.component').then((it) => it.ScholarComponent),
          },
          {
            path: 'details/:id',
            loadComponent: () =>
              import('./scholar/components/details/scholar-document-details.component').then((it) => it.ScholarDocumentDetailsComponent),
          },
        ],
      },
      {
        path: 'administration-panel',
        loadComponent: () => import('./admin/admin-panel.component').then((it) => it.AdminPanelComponent),
        canActivate: [dashboardAccessGuard, featureFlagGuard],
        data: {
          featureFlag: FeatureFlagType.PLATFORM_USAGE_PAGE_ENABLED,
        },
        children: [
          {
            path: '',
            redirectTo: 'llm-costs',
            pathMatch: 'full',
          },
          {
            path: 'llm-costs',
            loadComponent: () => import('./admin/components/llm-costs/llm-costs.component').then((it) => it.LlmCostsComponent),
            children: [
              { path: '', redirectTo: 'chart', pathMatch: 'full' },
              {
                path: 'chart',
                loadComponent: () =>
                  import('./admin/components/llm-costs/llm-costs-chart/llm-costs-chart.component').then((it) => it.LlmCostsChartComponent),
              },
              // {
              //   path: 'table',
              //   loadComponent: () =>
              //     import('./admin/components/llm-costs/llm-costs-table/llm-costs-table.component').then((it) => it.LlmCostsTableComponent),
              // },
            ],
          },
          {
            path: 'platform-usage',
            loadComponent: () => import('./admin/components/platform-usage/platform-usage.component').then((it) => it.PlatformUsageComponent),
          },
          {
            path: 'access-management',
            canActivate: [isIndagoAiTenantGuard],
            loadComponent: () =>
              import('./admin/components/access-management/access-management.component').then((it) => it.AccessManagementComponent),
          },
          // {
          //   path: 'research-assistant-usage',
          //   loadComponent: () =>
          //     import('./admin/components/research-assistant/research-assistant-usage.component').then((it) => it.ResearchAssistantUsageComponent),
          //   children: [
          //     {
          //       path: '',
          //       redirectTo: 'projects',
          //       pathMatch: 'full',
          //     },
          //     {
          //       path: 'projects',
          //       loadComponent: () =>
          //         import('./admin/components/research-assistant/streams/research-usage-streams.component').then(
          //           (it) => it.ResearchUsageStreamsComponent
          //         ),
          //       children: [
          //         { path: '', redirectTo: 'chart', pathMatch: 'full' },
          //         {
          //           path: 'chart',
          //           loadComponent: () =>
          //             import('./admin/components/research-assistant/streams/chart/research-streams-chart.component').then(
          //               (it) => it.ResearchStreamsChartComponent
          //             ),
          //         },
          //         {
          //           path: 'table',
          //           loadComponent: () =>
          //             import('./admin/components/research-assistant/streams/table/research-streams-table.component').then(
          //               (it) => it.ResearchStreamsTableComponent
          //             ),
          //         },
          //       ],
          //     },
          //     {
          //       path: 'branches',
          //       loadComponent: () =>
          //         import('./admin/components/research-assistant/branches/research-usage-branches.component').then(
          //           (it) => it.ResearchUsageBranchesComponent
          //         ),
          //       children: [
          //         { path: '', redirectTo: 'chart', pathMatch: 'full' },
          //         {
          //           path: 'chart',
          //           loadComponent: () =>
          //             import('./admin/components/research-assistant/branches/chart/research-branches-chart.component').then(
          //               (it) => it.ResearchBranchesChartComponent
          //             ),
          //         },
          //         {
          //           path: 'table',
          //           loadComponent: () =>
          //             import('./admin/components/research-assistant/branches/table/research-branches-table.component').then(
          //               (it) => it.ResearchBranchesTableComponent
          //             ),
          //         },
          //       ],
          //     },
          //   ],
          // },
          // {
          //   path: 'scholar-usage',
          //   loadComponent: () => import('./admin/components/scholar-usage/scholar-usage.component').then((it) => it.ScholarUsageComponent),
          //   children: [
          //     { path: '', redirectTo: 'chart', pathMatch: 'full' },
          //     {
          //       path: 'chart',
          //       loadComponent: () =>
          //         import('./admin/components/scholar-usage/scholar-usage-chart/scholar-usage-chart.component').then(
          //           (it) => it.ScholarUsageChartComponent
          //         ),
          //     },
          //     {
          //       path: 'table',
          //       loadComponent: () =>
          //         import('./admin/components/scholar-usage/scholar-usage-table/scholar-usage-table.component').then(
          //           (it) => it.ScholarUsageTableComponent
          //         ),
          //     },
          //   ],
          // },
        ],
      },
      {
        path: 'agents',
        canActivate: [permissionGuard],
        data: {
          permissionKey: 'agents',
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./agents/agents.component').then((it) => it.AgentsComponent),
          },
          {
            path: 'details/:agentId',
            canActivate: [permissionGuard],
            data: {
              permissionKey: 'agents.open',
            },
            loadComponent: () =>
              import('./agents/components/agent-details/agent-details.component').then((it) => it.AgentDetailsComponent),
          },
          {
            path: 'details/:agentId/conversation/:conversationId',
            canActivate: [permissionGuard],
            data: {
              permissionKey: 'agents.open',
            },
            loadComponent: () =>
              import('./agents/components/agent-details/agent-details.component').then((it) => it.AgentDetailsComponent),
          },
        ],
      },
      {
        path: 'vault',
        canActivate: [permissionGuard],
        data: {
          permissionKey: 'vault',
        },
        loadComponent: () => import('./vault/vault.component').then((it) => it.VaultComponent),
        children: [
          {
            path: '',
            loadComponent: () => import('./vault/components/list/vault-list.component').then((it) => it.VaultListComponent),
          },
          {
            path: ':vaultId',
            loadComponent: () => import('./vault/components/list/vault/vault-details.component').then((it) => it.VaultDetailsComponent),
          },
          {
            path: ':vaultId/:itemId',
            loadComponent: () =>
              import('./vault/components/list/vault/document/vault-document-details.component').then((it) => it.VaultDocumentDetailsComponent),
            children: [
              {
                path: '',
                redirectTo: 'file-preview',
                pathMatch: 'full',
              },
              {
                path: 'file-preview',
                loadComponent: () =>
                  import('./vault/components/list/vault/document/preview/vault-document-preview.component').then(
                    (it) => it.VaultDocumentPreviewComponent
                  ),
              },
              {
                path: 'file-summary',
                loadComponent: () =>
                  import('./vault/components/list/vault/document/summary/vault-document-summary.component').then(
                    (it) => it.VaultDocumentSummaryComponent
                  ),
                children: [
                  {
                    path: '',
                    redirectTo: 'view',
                    pathMatch: 'full',
                  },
                  {
                    path: 'view',
                    loadComponent: () =>
                      import('./vault/components/list/vault/document/summary/view/vault-document-summary-view.component').then(
                        (it) => it.VaultDocumentSummaryViewComponent
                      ),
                  },
                  {
                    path: 'generate',
                    loadComponent: () =>
                      import('./vault/components/list/vault/document/summary/new/vault-document-summary-new.component').then(
                        (it) => it.VaultDocumentSummaryNewComponent
                      ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'auth',
    loadComponent: () => import('./auth/auth.component').then((it) => it.AuthComponent),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'sign-in', pathMatch: 'full' },
      {
        path: 'sign-in',
        loadComponent: () => import('./auth/components/login/login.component').then((it) => it.LoginComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./auth/components/reset-password/reset-password.component').then((it) => it.ResetPasswordComponent),
      },
    ],
  },
  { path: '**', component: NotFoundComponent },
];
